import { useRef, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Popper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';

// Renders the primary action as a compact button and any further actions in
// a popper-based overflow menu. Designed to fit inline in the card header.
//
// Action shape: { label, icon, onClick, primary?, render? }
//   - `primary: true` marks the inline button (only ONE primary supported).
//   - `render` (optional) lets the scene supply a fully custom node — useful
//     for cases like Contracts where a NavLink is needed instead of onClick.

const OverviewActions = ({ actions = [] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchor = useRef(null);

  if (!actions || actions.length === 0) return null;

  // Pick the primary action (explicit flag, falls back to first item).
  const primary = actions.find((a) => a.primary) ?? actions[0];
  const overflow = actions.filter((a) => a !== primary);

  const stop = (e) => e.stopPropagation();

  return (
    <Box className="flex items-center gap-1" onClick={stop}>
      {primary &&
        (primary.render ? (
          primary.render()
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={primary.icon}
            onClick={primary.onClick}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              paddingX: 1.25,
              paddingY: 0.5,
              minHeight: 28,
              backgroundColor: `${colors.info}30`,
              border: `1px solid ${colors.info}80`,
              color: colors.info,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: `${colors.info}45`,
                border: `1px solid ${colors.info}`,
                boxShadow: 'none',
              },
            }}
          >
            {primary.label}
          </Button>
        ))}

      {overflow.length > 0 && (
        <>
          <Tooltip title={t('More')}>
            <IconButton
              ref={menuAnchor}
              size="small"
              onClick={(e) => {
                stop(e);
                setMenuOpen((v) => !v);
              }}
              sx={{
                color: colors.text,
                opacity: 0.7,
                '&:hover': { opacity: 1, backgroundColor: `${colors.selected}40` },
              }}
            >
              <MoreHorizIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
          <Popper
            open={menuOpen}
            anchorEl={menuAnchor.current}
            placement="bottom-end"
            sx={{ zIndex: 9999, paddingTop: '6px' }}
          >
            <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
              <Box
                className="rounded-lg backdrop-blur-lg p-1 flex flex-col min-w-[200px]"
                sx={{ backgroundColor: colors.cardSolid, boxShadow: 3 }}
              >
                {overflow.map((action, idx) =>
                  action.render ? (
                    <Box key={idx} onClick={() => setMenuOpen(false)}>
                      {action.render()}
                    </Box>
                  ) : (
                    <Box
                      key={idx}
                      onClick={() => {
                        setMenuOpen(false);
                        action.onClick?.();
                      }}
                      className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors"
                      sx={{
                        color: colors.text,
                        '&:hover': { backgroundColor: colors.card },
                      }}
                    >
                      {action.icon && (
                        <Box sx={{ display: 'flex', color: colors.text, opacity: 0.7 }}>
                          {action.icon}
                        </Box>
                      )}
                      <Typography variant="body2">{action.label}</Typography>
                    </Box>
                  ),
                )}
              </Box>
            </ClickAwayListener>
          </Popper>
        </>
      )}
    </Box>
  );
};

export default OverviewActions;
